/**
 * Focus and Escape rules shared by the kit's overlays (Dialog, Sheet, Popover).
 *
 * Initial focus (Dialog, Sheet). Radix would focus the first tabbable element of the whole surface, which is the
 * header's close button: the dialog would open on "Close" and Enter would dismiss it. The kit starts instead on
 *   1. the first enabled tabbable control of the body (a field to type into, a choice to make);
 *   2. else the footer's primary button (Enter confirms; never a danger button, so Enter cannot delete);
 *   3. else the surface itself (tabIndex -1): Escape, Tab and the screen reader all start inside it.
 * An element with `autoFocus` keeps its focus (Radix does not run the open focus when focus is already inside);
 * the opener that gets focus back on close is recorded by `useOpener`, which works in both cases.
 *
 * Escape with a pending edit. Radix closes a layer on Escape from a document capture listener, which runs before
 * the focused control's own key handler. Controls whose Escape has a local meaning (revert typed text, cancel a
 * scrub) mark themselves with `data-pending-edit` while that is the case (`pendingEditProps`); the overlays leave
 * that Escape to the control (`keepOpenForPendingEdit`) and close on the next one.
 */

import { useState } from 'react';

interface OpenerState {
  readonly open: boolean;
  readonly opener: HTMLElement | null;
}

/**
 * The element that had focus when `open` became true, for Dialog and Sheet (opened by services, without a Radix
 * trigger) to give focus back on close. It is read while rendering the open state, before an `autoFocus` control
 * inside the overlay moves focus (Radix then skips its open focus event, so that event cannot record it). It is
 * kept after closing so the close focus handler can still read it.
 */
export function useOpener(open: boolean): HTMLElement | null {
  const [state, setState] = useState<OpenerState>({ open: false, opener: null });
  if (state.open !== open) {
    const active = typeof document === 'undefined' ? null : document.activeElement;
    setState({ open, opener: open ? (active instanceof HTMLElement ? active : null) : state.opener });
  }
  return state.opener;
}

const TABBABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button',
  'input',
  'select',
  'textarea',
  'iframe',
  'summary',
  'audio[controls]',
  'video[controls]',
  '[tabindex]',
  '[contenteditable]:not([contenteditable="false"])',
].join(', ');

function isTabbable(el: HTMLElement): boolean {
  if (el.tabIndex < 0) return false;
  if (el.matches(':disabled')) return false;
  if (el instanceof HTMLInputElement && el.type === 'hidden') return false;
  if (el.closest('[hidden], [inert], [aria-hidden="true"]') !== null) return false;
  return getComputedStyle(el).visibility !== 'hidden';
}

/** Keyboard-reachable descendants of `root` in DOM order. */
export function tabbablesIn(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)].filter(isTabbable);
}

const isEnabled = (el: HTMLElement): boolean => el.getAttribute('aria-disabled') !== 'true';

/** Moves focus to `el`; true when it took focus (hidden or detached elements do not). */
function tryFocus(el: HTMLElement): boolean {
  el.focus({ preventScroll: true });
  if (document.activeElement !== el) return false;
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) el.select();
  return true;
}

export interface OverlayParts {
  /** The overlay surface (role="dialog"); focused last, made focusable with tabIndex -1 when needed. */
  readonly content: HTMLElement | null;
  readonly body: HTMLElement | null;
  readonly footer: HTMLElement | null;
}

/** Focuses the starting element of an overlay that just opened (see module comment); returns it. */
export function focusInitial(parts: OverlayParts): HTMLElement | null {
  const candidates: HTMLElement[] = [];
  if (parts.body !== null) candidates.push(...tabbablesIn(parts.body).filter(isEnabled).map(checkedOfGroup));
  if (parts.footer !== null) {
    const primary = parts.footer.querySelector<HTMLElement>('[data-variant="primary"]');
    if (primary !== null && isTabbable(primary) && isEnabled(primary)) candidates.push(primary);
  }
  for (const el of candidates) if (tryFocus(el)) return el;
  const content = parts.content;
  if (content === null) return null;
  if (!content.hasAttribute('tabindex')) content.tabIndex = -1;
  return tryFocus(content) ? content : null;
}

/**
 * A native radio stands for its group: focus goes to the group's checked radio, as Tab would (UX-022: otherwise the
 * first card of a radio-card group looks active next to the selected one).
 */
export function checkedOfGroup(el: HTMLElement): HTMLElement {
  if (!(el instanceof HTMLInputElement) || el.type !== 'radio' || el.checked || el.name === '') return el;
  const scope = el.form ?? el.ownerDocument;
  for (const r of scope.querySelectorAll<HTMLInputElement>('input[type="radio"]')) {
    if (r.name === el.name && r.checked && !r.disabled) return r;
  }
  return el;
}

/** Attribute a control sets while its Escape reverts a pending edit. */
export const PENDING_EDIT_ATTR = 'data-pending-edit';

/** Props marking a control whose Escape currently has a local meaning (spread onto the input). */
export function pendingEditProps(pending: boolean): { readonly 'data-pending-edit'?: 'true' } {
  return pending ? { [PENDING_EDIT_ATTR]: 'true' } : {};
}

/** True when Escape pressed now belongs to the focused control (it reverts a pending edit). */
export function escapeHasLocalMeaning(e?: KeyboardEvent): boolean {
  const target = e?.target instanceof Element ? e.target : document.activeElement;
  return (target?.closest(`[${PENDING_EDIT_ATTR}]`) ?? null) !== null;
}

/** onEscapeKeyDown for overlay layers: leaves this Escape to a control with a pending edit (layer stays open). */
export function keepOpenForPendingEdit(e: KeyboardEvent): void {
  if (escapeHasLocalMeaning(e)) e.preventDefault();
}
